import { IConfig } from "../../../utils/IConfig";
import { loadConfig } from "../../../utils/IConfigUtils";
import { IPrintParams } from "./IFooterParams";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const createPrintUrl = (params: IPrintParams, print: boolean, paramsOnly: boolean): string => {
    const config: IConfig = loadConfig();

    let colorsString = "";
    params.colors.forEach((color, index) => colorsString += `_c${index + 1}:${color.replace("#", "")}`);

    let shapesString = "";
    params.shapes.shapes.forEach((shape, index) => {
        const shapeName = shape.tagName === 'hidden' ? 'hidden' : shape.name;
        shapesString += `_shape${index + 1}-name:${shapeName}_shape${index + 1}-rotate:${shape.rotate}`;
    });

    let shapePositionsString = "";
    params.shapePositions.forEach((shape, index) => shapePositionsString += `_shape${index + 1}-left:${shape.left}_shape${index + 1}-top:${shape.top}`);

    let shapeColorsString = "";
    params.shapeColors.forEach((color, index) => shapeColorsString += `_shape${index + 1}-color:${color.replace("#", "")}`);

    let logoImg = ``;
    if(config.application.folderName && config.application.logoPdfFileName) {
        logoImg = `_logoUrl:${config.application.folderName}/${config.application.logoPdfFileName}`;
    }

    const urlPath = window.location.pathname;
    const pathSegments = urlPath.split('/');
    const languages = ["en", "fr", "es", "ko", "zh", "pl", "pt"]
    const languageFromUrl = pathSegments[1] && languages.includes(pathSegments[1])
    ? pathSegments[1]
    : pathSegments[2];
    let languageParam = `${languageFromUrl ? languageFromUrl : config.application.selectedLanguage}`;
    let languageString = `_lang:${languageParam}`

    let justParams = `?print=${print}_intro:false_stripesWidth:${params.stripesWidth}${colorsString}${shapesString}${shapePositionsString}${languageString}${shapeColorsString}${logoImg}`;
    let baseUrl = config.application.remoteAppUrl || config.environment.selfUrlForPrintService;
    let urlForPrint = `${baseUrl}${languageParam}${justParams}`;
    return print ? `${config.environment.printServiceUrl}?url=${urlForPrint}` : (paramsOnly ? justParams : urlForPrint);
}

const download = (href: string) => {
    let link = document.createElement('a');
    link.href = href;
    link.download = 'file.pdf';
    link.dispatchEvent(new MouseEvent('click'));
}

const downloadPdf = (params: IPrintParams) => {
    download(createPrintUrl(params, true, false))
}

const renderQr = (params: IPrintParams) => {
    let link = document.createElement('a');
    var qrcode = createPrintUrl(params, false, false)
        // aditinoal step to generate download link from button not directly
        .replace("print=false", "download=true");
    var href = `/?qrcode=${qrcode}&params=${createPrintUrl(params, false, true)}`;
    link.href = href;
    link.dispatchEvent(new MouseEvent('click'));
}

const getShareUrl = (params: IPrintParams) => {
    const config: IConfig = loadConfig();
    var origin = config.application.remoteAppUrl || window.location.origin;
    return `${origin}${createPrintUrl(params, false, true)}`;
}


// A4 @ 150 DPI = 1240 × 1754 px — ideal for A4 print, visibly pixelated when upscaled to A1.
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const A4_RASTER_WIDTH_PX = 1240;
const A4_RASTER_HEIGHT_PX = 1754;

const getFittedPageLayout = (contentWidth: number, contentHeight: number, pageWidth: number, pageHeight: number) => {
    const contentAspect = contentWidth / contentHeight;

    let width = pageWidth;
    let height = pageWidth / contentAspect;

    if (height > pageHeight) {
        height = pageHeight;
        width = pageHeight * contentAspect;
    }

    return {
        width,
        height,
        offsetX: (pageWidth - width) / 2,
        offsetY: (pageHeight - height) / 2,
    };
};

const getA4RasterSize = (contentWidth: number, contentHeight: number) => {
    const isLandscape = contentWidth > contentHeight;
    return {
        width: isLandscape ? A4_RASTER_HEIGHT_PX : A4_RASTER_WIDTH_PX,
        height: isLandscape ? A4_RASTER_WIDTH_PX : A4_RASTER_HEIGHT_PX,
    };
};

const getA4RasterCaptureScale = (contentWidth: number, contentHeight: number) => {
    const rasterSize = getA4RasterSize(contentWidth, contentHeight);
    return Math.min(rasterSize.width / contentWidth, rasterSize.height / contentHeight);
};

const renderPrintContentCanvas = async (printContainer: HTMLElement, contentWidth: number, contentHeight: number) => {
    const previousBodyTransform = document.body.style.transform;
    const previousBodyTransformOrigin = document.body.style.transformOrigin;

    document.body.style.transform = 'none';
    document.body.style.transformOrigin = 'top left';

    try {
        return await html2canvas(printContainer, {
            scale: getA4RasterCaptureScale(contentWidth, contentHeight),
            useCORS: true,
            backgroundColor: '#ffffff',
            width: contentWidth,
            height: contentHeight,
        });
    } finally {
        document.body.style.transform = previousBodyTransform;
        document.body.style.transformOrigin = previousBodyTransformOrigin;
    }
};

const drawCanvasOnA4Page = (contentCanvas: HTMLCanvasElement) => {
    const pageSize = getA4RasterSize(contentCanvas.width, contentCanvas.height);
    const layout = getFittedPageLayout(contentCanvas.width, contentCanvas.height, pageSize.width, pageSize.height);
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = pageSize.width;
    pageCanvas.height = pageSize.height;

    const context = pageCanvas.getContext('2d');
    if (!context) {
        return contentCanvas;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, pageSize.width, pageSize.height);
    context.drawImage(contentCanvas, layout.offsetX, layout.offsetY, layout.width, layout.height);
    return pageCanvas;
};

const drawTextOnCanvas = (
    canvas: HTMLCanvasElement,
    text: string,
    x: number,
    baselineY: number,
    fontSize: number
) => {
    const context = canvas.getContext('2d');
    if (!context || !text) return;

    context.save();
    context.fillStyle = '#000000';
    context.font = `${fontSize}px Arial, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';
    context.fillText(text, x, baselineY);
    context.restore();
};

const drawSignatureTextOnA4Canvas = (canvas: HTMLCanvasElement, text: string) => {
    drawTextOnCanvas(canvas, text, canvas.width / 2, canvas.height - 20, 22);
};

const prepareSignatureClone = (signatureClone: HTMLElement) => {
    signatureClone.classList.remove('footer--print');
    signatureClone.style.display = 'flex';
    signatureClone.style.flexDirection = 'column';
    signatureClone.style.alignItems = 'center';
    signatureClone.style.width = '100%';
    signatureClone.style.paddingBottom = '20px';

    const logos = signatureClone.querySelectorAll('.footer-logo');
    logos.forEach(logo => {
        const logoEl = logo as HTMLElement;
        logoEl.style.position = 'static';
        logoEl.style.left = 'auto';
        logoEl.style.transform = 'none';
        logoEl.style.margin = '5px auto 34px auto';
        logoEl.style.display = 'block';
    });

    const artworkLogos = signatureClone.querySelectorAll('.footer-logo--artwork');
    artworkLogos.forEach(logo => {
        const logoEl = logo as HTMLElement;
        logoEl.style.marginLeft = '0';
        logoEl.style.display = 'none';
    });

    const logoWrapper = signatureClone.querySelector('.footer-logo--wrapper') as HTMLElement;
    if (logoWrapper) {
        logoWrapper.style.justifyContent = 'center';
        logoWrapper.style.alignItems = 'center';
        logoWrapper.style.flexDirection = 'column';
        logoWrapper.style.minHeight = 'auto';
        logoWrapper.style.width = '100%';
    }

    Array.from(logoWrapper?.children || []).forEach((child) => {
        const childEl = child as HTMLElement;
        if (!childEl.classList.contains('footer-logo')) {
            childEl.style.display = 'none';
        }
    });

    let interpretedBy = signatureClone.querySelector('.footer__interpreted-by') as HTMLElement;
    if (!interpretedBy) {
        interpretedBy = document.createElement('p');
        interpretedBy.className = 'footer__interpreted-by';
    }

    if (interpretedBy) {
        if (!interpretedBy.textContent || interpretedBy.textContent.trim().replace(/_/g, '').length === 0) {
            interpretedBy.textContent = 'Interpreted by____________________';
        }
        signatureClone.appendChild(interpretedBy);
        interpretedBy.style.display = 'block';
        interpretedBy.style.position = 'static';
        interpretedBy.style.visibility = 'visible';
        interpretedBy.style.opacity = '1';
        interpretedBy.style.width = '100%';
        interpretedBy.style.textAlign = 'center';
        interpretedBy.style.color = '#000000';
        interpretedBy.style.fontSize = '14px';
        interpretedBy.style.lineHeight = '20px';
        interpretedBy.style.margin = '0';
    }
};

const downloadRasterPdf = async (): Promise<void> => {
    const workspace = document.getElementById('artwork-workspace');
    if (!workspace) {
        console.error('Workspace element not found');
        return;
    }

    const signatureElement = document.querySelector('.footer--signature') as HTMLElement;

    const domWidth = workspace.offsetWidth;

    const printContainer = document.createElement('div');
    printContainer.style.position = 'absolute';
    printContainer.style.left = '-9999px';
    printContainer.style.top = '0';
    printContainer.style.width = domWidth + 'px';
    printContainer.style.backgroundColor = '#ffffff';

    const headerElement = document.querySelector('.header') as HTMLElement;
    if (headerElement) {
        const headerClone = headerElement.cloneNode(true) as HTMLElement;
        headerClone.classList.remove('header--print');
        headerClone.style.display = 'flex';
        headerClone.style.padding = '20px 0';
        headerClone.style.width = '100%';
        headerClone.style.justifyContent = 'center';
        printContainer.appendChild(headerClone);
    }

    const workspaceClone = workspace.cloneNode(true) as HTMLElement;
    printContainer.appendChild(workspaceClone);

    if (signatureElement) {
        const signatureClone = signatureElement.cloneNode(true) as HTMLElement;
        prepareSignatureClone(signatureClone);
        printContainer.appendChild(signatureClone);
    }

    document.body.appendChild(printContainer);

    try {
        // Enforce crossOrigin to avoid Tainted Canvas Security Errors from Azure blob images
        const imgs = printContainer.querySelectorAll('img');
        imgs.forEach((img) => {
            if (!img.crossOrigin && img.src && img.src.startsWith('http') && !img.src.startsWith(window.location.origin)) {
                img.crossOrigin = 'anonymous';
            }
        });

        // After appending to body, we can accurately measure the actual rendered height
        const totalHeight = printContainer.offsetHeight || (workspace.offsetHeight + 100);

        const canvas = await renderPrintContentCanvas(printContainer, domWidth, totalHeight);

        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const capturedW = canvas.width;
        const capturedH = canvas.height;

        // Detect ideal orientation based on captured image aspect ratio
        const isLandscape = capturedW > capturedH;
        const currentA4Width = isLandscape ? A4_HEIGHT_MM : A4_WIDTH_MM;
        const currentA4Height = isLandscape ? A4_WIDTH_MM : A4_HEIGHT_MM;

        const layout = getFittedPageLayout(capturedW, capturedH, currentA4Width, currentA4Height);

        const pdf = new jsPDF({
            orientation: isLandscape ? 'landscape' : 'portrait',
            unit: 'mm',
            format: 'a4',
        });

        pdf.addImage(imgData, 'JPEG', layout.offsetX, layout.offsetY, layout.width, layout.height);

        // Use manual blob trigger to avoid Edge/Chrome assigning temporary GUID filenames without extension
        const blob = pdf.output('blob');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const now = new Date();
        const dateStr = `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}-${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
        link.download = `cruz-diez-artwork-${dateStr}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error("PDF Export failed: ", err);
        alert("Błąd podczas generowania PDF. Spróbuj chwilę odczekać lub odświeżyć stronę. Problem z plikiem: " + (err as Error)?.message);
    } finally {
        if (printContainer.parentNode) {
            document.body.removeChild(printContainer);
        }
    }
};

const downloadRasterImage = async (): Promise<void> => {
    const workspace = document.getElementById('artwork-workspace');
    if (!workspace) {
        console.error('Workspace element not found');
        return;
    }

    const signatureElement = document.querySelector('.footer--signature') as HTMLElement;

    const domWidth = workspace.offsetWidth;

    const printContainer = document.createElement('div');
    printContainer.style.position = 'absolute';
    printContainer.style.left = '-9999px';
    printContainer.style.top = '0';
    printContainer.style.width = domWidth + 'px';
    printContainer.style.backgroundColor = '#ffffff';

    const headerElement = document.querySelector('.header') as HTMLElement;
    if (headerElement) {
        const headerClone = headerElement.cloneNode(true) as HTMLElement;
        headerClone.classList.remove('header--print');
        headerClone.style.display = 'flex';
        headerClone.style.padding = '20px 0';
        headerClone.style.width = '100%';
        headerClone.style.justifyContent = 'center';
        printContainer.appendChild(headerClone);
    }

    const workspaceClone = workspace.cloneNode(true) as HTMLElement;
    printContainer.appendChild(workspaceClone);

    if (signatureElement) {
        const signatureClone = signatureElement.cloneNode(true) as HTMLElement;
        prepareSignatureClone(signatureClone);
        printContainer.appendChild(signatureClone);
    }

    document.body.appendChild(printContainer);

    try {
        const imgs = printContainer.querySelectorAll('img');
        imgs.forEach((img) => {
            if (!img.crossOrigin && img.src && img.src.startsWith('http') && !img.src.startsWith(window.location.origin)) {
                img.crossOrigin = 'anonymous';
            }
        });

        const totalHeight = printContainer.offsetHeight || (workspace.offsetHeight + 100);

        const canvas = await renderPrintContentCanvas(printContainer, domWidth, totalHeight);

        const imgData = canvas.toDataURL('image/jpeg', 0.92);

        const now = new Date();
        const dateStr = `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}-${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
        const fileName = `cruz-diez-artwork-${dateStr}.jpg`;

        if (navigator.share) {
            try {
                const response = await fetch(imgData);
                const blob = await response.blob();
                const file = new File([blob], fileName, { type: "image/jpeg" });

                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        files: [file],
                        title: 'Cruz Diez Artwork'
                    });
                } else {
                    throw new Error("Sharing files is not supported");
                }
            } catch (err) {
                console.log("Share API failed or rejected, falling back to direct download:", err);
                const link = document.createElement('a');
                link.href = imgData;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } else {
            const link = document.createElement('a');
            link.href = imgData;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    } catch (err) {
        console.error("Image Export failed: ", err);
        alert("Błąd podczas generowania obrazu. Problem z plikiem: " + (err as Error)?.message);
    } finally {
        if (printContainer.parentNode) {
            document.body.removeChild(printContainer);
        }
    }
};

export const generateArtworkDataUrl = async (): Promise<string | null> => {
    const workspace = document.getElementById('artwork-workspace');
    if (!workspace) return null;

    const signatureElement = document.querySelector('.footer--signature') as HTMLElement;
    const domWidth = workspace.offsetWidth;

    const printContainer = document.createElement('div');
    printContainer.style.position = 'absolute';
    printContainer.style.left = '-9999px';
    printContainer.style.top = '0';
    printContainer.style.width = domWidth + 'px';
    printContainer.style.backgroundColor = '#ffffff';
    let interpretedByText: string | null = null;

    const headerElement = document.querySelector('.header') as HTMLElement;
    if (headerElement) {
        const headerClone = headerElement.cloneNode(true) as HTMLElement;
        headerClone.classList.remove('header--print');
        headerClone.style.display = 'flex';
        headerClone.style.padding = '20px 0';
        headerClone.style.width = '100%';
        headerClone.style.justifyContent = 'center';
        printContainer.appendChild(headerClone);
    }

    const workspaceClone = workspace.cloneNode(true) as HTMLElement;
    printContainer.appendChild(workspaceClone);

    if (signatureElement) {
        const signatureClone = signatureElement.cloneNode(true) as HTMLElement;
        prepareSignatureClone(signatureClone);
        printContainer.appendChild(signatureClone);
    }

    document.body.appendChild(printContainer);
    let dataUrl: string | null = null;

    try {
        const imgs = printContainer.querySelectorAll('img');
        imgs.forEach((img) => {
            if (!img.crossOrigin && img.src && img.src.startsWith('http') && !img.src.startsWith(window.location.origin)) {
                img.crossOrigin = 'anonymous';
            }
        });

        const totalHeight = printContainer.offsetHeight || (workspace.offsetHeight + 100);
        const interpretedBy = printContainer.querySelector('.footer__interpreted-by') as HTMLElement;
        if (interpretedBy) {
            interpretedByText = interpretedBy.textContent || 'Interpreted by____________________';
            interpretedBy.style.color = 'transparent';
        }

        const canvas = await renderPrintContentCanvas(printContainer, domWidth, totalHeight);
        const pageCanvas = drawCanvasOnA4Page(canvas);
        if (interpretedByText) {
            drawSignatureTextOnA4Canvas(pageCanvas, interpretedByText);
        }

        dataUrl = pageCanvas.toDataURL('image/jpeg', 0.92);
    } catch (err) {
        console.error("Image Export/Upload failed: ", err);
    } finally {
        if (printContainer.parentNode) {
            document.body.removeChild(printContainer);
        }
    }

    return dataUrl;
};

export { downloadPdf, download, renderQr, getShareUrl, createPrintUrl, downloadRasterPdf, downloadRasterImage, getFittedPageLayout, getA4RasterCaptureScale, drawTextOnCanvas, drawSignatureTextOnA4Canvas }
